// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useCallback, useMemo, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Settings, Monitor, FileCode } from "lucide-react"
import type { Config } from '@/api/types/config'
import { ModeSettings } from './workspace/mode-settings'
import { PromptSettings } from './workspace/prompt-settings'
import { SchemaEditor } from './workspace/schema'

interface ConfigFormProps {
  config?: Config
  onSubmit: (data: Partial<Config>) => Promise<void>
  isLoading: boolean
}

// Standardized interface for all child components
export interface ConfigComponentProps {
  config: Config | undefined
  onUpdate: (updates: Partial<Config>) => void
  isLoading: boolean
}

export function ConfigForm({ config, onSubmit, isLoading }: ConfigFormProps) {
  const [configUpdates, setConfigUpdates] = useState<Partial<Config>>({})
  const [isDirty, setIsDirty] = useState(false)

  // Reset pending updates when workspace changes
  useEffect(() => {
    if (config?.sk) {
      console.log('[ConfigForm] Workspace changed, clearing pending updates:', config.sk)
      setConfigUpdates({})
      setIsDirty(false)
    }
  }, [config?.sk])

  const handleUpdate = useCallback((updates: Partial<Config>) => {
    console.log('[ConfigForm] handleUpdate called with:', Object.keys(updates))
    setConfigUpdates(prev => ({ ...prev, ...updates }))
    setIsDirty(true)
  }, [])
  
  // Create merged config that includes pending updates
  // But we need to be careful about deep merging prompt_config
  const mergedConfig = useMemo(() => {
    if (!config) return config

    const merged = { ...config }

    // Deep merge the prompt_config if it exists in updates
    if (configUpdates.prompt_config) {
      merged.prompt_config = {
        ...config.prompt_config,
        ...configUpdates.prompt_config
      }
    }

    // Merge other top-level properties - only merge defined values
    Object.keys(configUpdates).forEach(key => {
      if (key !== 'prompt_config') {
        const value = (configUpdates as any)[key]
        // Only override if the value is defined (not null or undefined)
        if (value !== undefined && value !== null) {
          (merged as any)[key] = value
        }
      }
    })

    return merged
  }, [config, configUpdates])
  

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    console.log('[ConfigForm] handleSubmit called, isDirty:', isDirty)
    if (!isDirty) return
    
    await onSubmit(configUpdates)
    setConfigUpdates({})
    setIsDirty(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Workspace Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="general">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="general" className="flex-1 whitespace-nowrap text-gray-800 dark:text-gray-200 data-[state=active]:bg-primary data-[state=active]:text-white font-medium">
                <Settings className="h-4 w-4" />
                General
              </TabsTrigger>
              <TabsTrigger value="schema" className="flex-1 whitespace-nowrap text-gray-800 dark:text-gray-200 data-[state=active]:bg-primary data-[state=active]:text-white font-medium">
                <FileCode className="h-4 w-4" />
                Schema
              </TabsTrigger>
              <TabsTrigger value="prompts" className="flex-1 whitespace-nowrap text-gray-800 dark:text-gray-200 data-[state=active]:bg-primary data-[state=active]:text-white font-medium">
                <Monitor className="h-4 w-4" />
                Prompts
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="general" className="space-y-4">
              <ModeSettings
                key={config?.sk}
                config={mergedConfig}
                onUpdate={handleUpdate}
                isLoading={isLoading}
              />
            </TabsContent>
            
            <TabsContent value="schema">
              <div className="space-y-4">
                <SchemaEditor 
                  config={mergedConfig} 
                  onUpdate={handleUpdate} 
                  isLoading={isLoading}
                />
              </div>
            </TabsContent>
            
            <TabsContent value="prompts">
              <PromptSettings 
                config={mergedConfig} 
                onUpdate={handleUpdate} 
                isLoading={isLoading} 
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={!isDirty || isLoading}
          className="bg-blue-700 hover:bg-blue-800 text-white font-medium"
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  )
}
